//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

//  emit WithdrawRequested(owner, assets, shares);
import * as yup from 'yup'
import { InferType } from 'yup'
import { yupEthAddr } from './Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export enum TransferStepNames {
  Source_Approval_Pending = 'Source Approval Pending',
  Source_Approved = 'Source Approved',
  Destination_Approval_Pending = 'Destination Approval Pending',
  Destination_Approved = 'Destination Approved',
  Source_Burn_Pending = 'Source Burn Pending',
  Source_Burned = 'Source Burned',
  Destination_Mint_Pending = 'Destination Mint Pending',
  Destination_Minted = 'Destination Minted',
}
export enum TransferStepNumbers {
  Source_Approval_Pending = 0,
  Source_Approved = 1,
  Destination_Approval_Pending = 2,
  Destination_Approved = 3,
  Source_Burn_Pending = 4,
  Source_Burned = 5,
  Destination_Mint_Pending = 6,
  Destination_Minted = 7,
}

export const TransferStepToString: Record<number, string> = {
  0: TransferStepNames.Source_Approval_Pending,
  1: TransferStepNames.Source_Approved,
  2: TransferStepNames.Destination_Approval_Pending,
  3: TransferStepNames.Destination_Approved,
  4: TransferStepNames.Source_Burn_Pending,
  5: TransferStepNames.Source_Burned,
  6: TransferStepNames.Destination_Mint_Pending,
  7: TransferStepNames.Destination_Minted,
}
export const PoolCrossChainTransferSchema = yup.object({
  poolAddr: yupEthAddr
    .required()
    .label('PoolAddr For target Chain')
    .meta(toFieldMetaData({ propName: 'poolAddr' })),
  sourcePoolAddr: yupEthAddr
    .required()
    .label('Source Pool Address')
    .meta(toFieldMetaData({ propName: 'sourcePoolAddr' })),

  timestamp: yup
    .number()
    .required()
    .label('Offchain Data Timestamp')
    .meta(toFieldMetaData({ propName: 'timestamp', stringType: StringTypeEnum.UNIX_DATE })),

  sourceChainId: yup
    .number()
    .required()
    .label('Source Chain Id')
    .meta(toFieldMetaData({ propName: 'sourceChainId' })),
  sourceDeploymentName: yup
    .string()
    .required()
    .label('Source Deployment Name')
    .meta(toFieldMetaData({ propName: 'sourceDeploymentName' })),

  sourceWalletAddr: yupEthAddr
    .required()
    .label('Source Wallet Address')
    .meta(toFieldMetaData({ propName: 'sourceWalletAddr' })),

  destinationPoolAddr: yupEthAddr //
    .required()
    .label('Destination Pool Address')
    .meta(toFieldMetaData({ propName: 'destinationPoolAddr' })),

  destinationChainId: yup
    .number()
    .required()
    .label('Destination Chain Id')
    .meta(toFieldMetaData({ propName: 'destinationChainId' })),
  destinationDeploymentName: yup
    .string()
    .required()
    .label('Destination Deployment Name')
    .meta(toFieldMetaData({ propName: 'destinationDeploymentName' })),

  destinationWalletAddr: yupEthAddr //
    .required()
    .label('Destination Wallet Address')
    .meta(toFieldMetaData({ propName: 'destinationWalletAddr' })),

  amount: yup
    .number()
    .required()
    .label('Target Amount')
    .meta(toFieldMetaData({ propName: 'amount' })),
  transferStep: yup
    .number()
    .label('Transfer Step')
    .required()
    .meta(
      toFieldMetaData({ propName: 'transferStep', description: 'Transfer Step ', stateToString: TransferStepToString })
    ),
  transferStepName: yup
    .string()
    .label('Transfer Step Name')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'transferStepName',
        description: 'Transfer Step Name',
      })
    ),
})

export const CollateralSchema = yup.object({
  collateralType: yup
    .string()
    .required()
    .label('Collateral Type')
    .meta(toFieldMetaData({ propName: 'collateralType' })),
  collateralId: yup
    .string()
    .required()
    .label('Collateral Id')
    .meta(toFieldMetaData({ propName: 'collateralId' })),
})
export const PoolOffchainDataSchemaValues: Record<string, yup.Schema> = {
  poolAddr: yupEthAddr
    .required()
    .label('Pool Address')
    .meta(toFieldMetaData({ propName: 'poolAddr' })),

  timestamp: yup
    .number()
    .required()
    .label('Offchain Data Timestamp')
    .meta(toFieldMetaData({ propName: 'timestamp', stringType: StringTypeEnum.UNIX_DATE })),

  collateral: yup
    .array()
    .of(CollateralSchema)
    .required()
    .label('Collateral')
    .meta(toFieldMetaData({ propName: 'collateral' })),

  benchmarkRate: yup
    .string()
    .label('Benchmark Rate')
    .meta({
      defaultDisplayValue: 'N/A',
      ...toFieldMetaData({ propName: 'benchmarkRate' }),
    }),

  transferInDays: yup
    .number()
    .optional()
    .label('Advance Processing Time (Days)')
    .meta({
      defaultDisplayValue: '0',
      ...toFieldMetaData({
        propName: 'transferInDays',
        description: 'The number of business days that will lapse before a loan starts accruing interest. ',
      }),
    }),
  transferOutDays: yup
    .number()
    .optional()
    .label('Withdrawal Processing Time (Days)')
    .meta({
      defaultDisplayValue: '0',
      ...toFieldMetaData({
        propName: 'transferOutDays',
        description: 'The number of business days that will lapse before a loan is due to be repaid. ',
      }),
    }),

  advisoryFeeRate: yup
    .number()
    .label('Advisory Fee Rate')
    .meta(toFieldMetaData({ propName: 'advisoryFeeRate', stringType: StringTypeEnum.OFFCHAIN_RATE })),
  platformFeeRate: yup
    .number()
    .label('Platform Fee Rate')
    .meta(toFieldMetaData({ propName: 'platformFeeRate', stringType: StringTypeEnum.OFFCHAIN_RATE })),
  liquidityFeeRate: yup
    .number()
    .label('Liquidity Fee Rate')
    .meta(toFieldMetaData({ propName: 'liquidityFeeRate', stringType: StringTypeEnum.OFFCHAIN_RATE })),
  contractualCurrencty: yup
    .string()
    .label('Contractual Currency')
    .meta({
      defaultDisplayValue: 'N/A',
      ...toFieldMetaData({ propName: 'contractualCurrencty' }),
    }),
  minimumTransferAmount: yup
    .number()
    .label('Minimum Transfer Amount')
    .meta(toFieldMetaData({ propName: 'minimumTransferAmount' })),
  maximumTransferAmount: yup
    .number()
    .label('Maximum Transfer Amount')
    .meta(toFieldMetaData({ propName: 'maximumTransferAmount' })),
  earlyWithdrawalProcessingPeriod: yup
    .number()
    .label('Early Withdrawal Processing Period')
    .meta(
      toFieldMetaData({ propName: 'earlyWithdrawalProcessingPeriod', stringType: StringTypeEnum.TIME_INTERVAL_DAYS })
    ),
  automaticRollover: yup
    .boolean()
    .label('Automatic Rollover')
    .meta(toFieldMetaData({ propName: 'automaticRollover' })),
  rolloverCollateral: yup
    .string()
    .label('Rollover Collateral')
    .meta(toFieldMetaData({ propName: 'rolloverCollateral' })),
}

export const PoolOffchainLenderDataSchemaValues: Record<string, yup.Schema> = {
  // collateral: yup
  //   .array()
  //   .of(CollateralSchema)
  //   .required()
  //   .label('Collateral')
  //   .meta(toFieldMetaData({ propName: 'collateral' })),
}

export const PoolOffchainDataSchema = yup.object(PoolOffchainDataSchemaValues)

export function PoolOffchainDataReportingSchema(properties?: string[]): yup.ObjectSchema<any> {
  if (!properties) {
    return yup.object({
      ...PoolOffchainDataSchemaValues,
    })
  } else {
    const schemaValues: Record<string, yup.Schema> = {}
    properties.forEach((prop) => {
      if (PoolOffchainDataSchemaValues[prop]) {
        schemaValues[prop] = PoolOffchainDataSchemaValues[prop]
      }

      if (!schemaValues[prop]) {
        throw new Error(`Property ${prop} not found in PoolReportingAccountCTOSchema`)
      }
    })

    return yup.object(schemaValues)
  }
}

export function PoolOffchainDataLenderReportingSchema(properties?: string[]): yup.ObjectSchema<any> {
  if (!properties) {
    return yup.object({
      ...PoolOffchainLenderDataSchemaValues,
    })
  } else {
    const schemaValues: Record<string, yup.Schema> = {}
    properties.forEach((prop) => {
      if (PoolOffchainLenderDataSchemaValues[prop]) {
        schemaValues[prop] = PoolOffchainLenderDataSchemaValues[prop]
      }

      if (!schemaValues[prop]) {
        throw new Error(`Property ${prop} not found in PoolReportingAccountCTOSchema`)
      }
    })

    return yup.object(schemaValues)
  }
}

export interface CollateralCTO extends InferType<typeof CollateralSchema> {
  collateralType: string
  collateralId: string
}

export interface PoolOffchainDataCTO extends InferType<typeof PoolOffchainDataSchema> {
  poolAddr: EthAddr
  collateral: CollateralCTO[]
}
export interface PoolCrossChainTransferCTO extends InferType<typeof PoolCrossChainTransferSchema> {
  poolAddr: EthAddr
  sourcePoolAddr: EthAddr
  destinationPoolAddr: EthAddr
}
